import React from 'react'
import styled from 'styled-components'

import {
  Box,
  BoxProps,
  Button,
  ButtonProps,
  Link as FuegokitLink,
  LinkProps,
  AtlasButton,
  Text,
  SxProp,
  sx,
  themeGet
} from '../..'

// Root
// ------------------------

export interface HelpRequestProps {
  /**
   * Items to render in the Help request, which should consist of a navigational link.
   */
  children?: React.ReactNode
  /**
   * The value of the help request text that should be active when initially rendered.
   */
  defaultValue?: string

  /**
   * A visual that renders inside the Help Request after the default value and link
   */
  trailingVisual?: string | React.ComponentType<React.PropsWithChildren<{className?: string}>>
}
/**
 * Displays a help request, link that can be an href or a route, and an optional trailing visual
 */
const HelpRequest = ({
  children,
  defaultValue = "Can't find what you need?",
  trailingVisual: TrailingVisual,
  ...props
}: HelpRequestProps) => {
  const hasTrailingVisual = Boolean(TrailingVisual)
  return (
    <Wrapper>
      <Text>{defaultValue}</Text>
      {children}

      {hasTrailingVisual ? (
        <Box sx={{color: 'icon.information'}}>
          {typeof TrailingVisual === 'function' ? <TrailingVisual /> : TrailingVisual}
        </Box>
      ) : null}
    </Wrapper>
  )
}

const Wrapper = styled(Box)`
  display: flex;
  align-items: center;
  justify-content: flex-end;
  width: 100%;
`

// Help Link
// ------------------------
export type HelpLinkProps = {
  /**
   * the label to display for the help link
   */
  children?: React.ReactNode
  /**
   * Links can be an anchor tag or a route, so these can be href strings or route strings
   */
  href?: string
  /**
   * Event handler called when the user selects a link (via mouse or keyboard).
   */
  onSelect?: (event: Event) => void
}
/**
 * Navigation link item for the Help Request
 */
const Link = ({children, href, onSelect, ...props}: HelpLinkProps) => {
  if (href && onSelect) {
    throw new Error(
      `Help request links take an onSelect or an href prop, but not both. See: https://fuegokit.design/react-storybook/patterns/rule-builder.`
    )
  }
  if (href) {
    return (
      <StyledHelpButton href={href} {...props}>
        {children}
      </StyledHelpButton>
    )
  } else if (onSelect) {
    return (
      <StyledHelpButton onClick={onSelect} {...props}>
        {children}
      </StyledHelpButton>
    )
  } else {
    throw new Error(
      `Provide HelpRequest.Link an onSelect or an href prop. See: https://fuegokit.design/react/patterns/global-header.`
    )
  }
}
const StyledHelpButton = styled(Button)<HelpLinkProps & ButtonProps & SxProp>`
  appearance: none;
  margin: 0;
  padding: 0;
  border: none;
  background: transparent;
  cursor: pointer;
  text-align: left;
  font: inherit;

  display: inline-block;
  color: ${themeGet('colors.link.pressed')};

  font-size: ${themeGet('fontSizes.3')};
  text-decoration: none;
  opacity: 0.85;
  font-weight: ${themeGet('fontWeights.semibold')};
  padding: 10px 12px;
  border-radius: ${themeGet('radii.1')};
  &:hover,
  &:focus {
    opacity: 1;
    text-decoration: none;
    color: ${themeGet('colors.link.pressed')};
    background-color: ${themeGet('colors.background.neutral.subtle.default')};
  }
  &:active {
    opacity: 1;
    text-decoration: none;
    background-color: ${themeGet('colors.background.selected.default.[default]')};
    color: ${themeGet('colors.text.selected')};
  }
  &:focus,
  .focus-visible {
    outline: none;
    transition-duration: 0s, 0.2s;
    box-shadow: 0 0 0 2px ${themeGet('colors.border.focused')};
  }
  &:focus:not(:focus-visible):not(.focus-visible) {
    box-shadow: 0 0 0 2px ${themeGet('colors.border.focused')};
  }
  ${sx}
`
// const StyledHelpLink = styled(FuegokitLink)<HelpLinkProps & LinkProps & SxProp>`
//   appearance: none;
//   margin: 0;
//   padding: 0;
//   border: none;
//   background: transparent;
//   cursor: pointer;
//   text-align: left;
//   font: inherit;

//   display: inline-block;
//   color: ${themeGet('colors.link.pressed')};
//   font-weight: normal;
//   font-size: ${themeGet('fontSizes.2')};
//   text-decoration: none;
//   opacity: 0.85;
//   font-weight: ${themeGet('fontWeights.bold')};
//   padding: 10px 12px;
//   border-radius: ${themeGet('radii.1')};
//   &:hover,
//   &:focus {
//     opacity: 1;
//     text-decoration: none;
//     color: ${themeGet('colors.link.pressed')};
//     background-color: ${themeGet('colors.background.neutral.subtle.hovered')};
//   }
//   &:active {
//     opacity: 1;
//     text-decoration: none;
//     background-color: ${themeGet('colors.background.selected.default.[default]')};
//     color: ${themeGet('colors.text.selected')};
//   }
//   &:focus,
//   .focus-visible {
//     outline: none;
//     transition-duration: 0s, 0.2s;
//     box-shadow: 0 0 0 2px ${themeGet('colors.border.focused')};
//   }
//   &:focus:not(:focus-visible):not(.focus-visible) {
//     box-shadow: 0 0 0 2px ${themeGet('colors.border.focused')};
//   }
//   ${sx}
// `

HelpRequest.displayName = 'HelpRequest'
Link.displayName = 'HelpRequest.Link'
export default Object.assign(HelpRequest, {Link})
